import { useState, useEffect } from 'react';
import { Calendar, Clock, User, Phone, Mail, Scissors, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { API_ENDPOINTS, isUsingFallback } from '../utils/api';
import { dbBookings, type Booking as DbBooking } from '../utils/dbBackend';

interface Booking {
  id: string;
  name: string;
  phone: string;
  email: string;
  service: string;
  date: string;
  time: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  createdAt: string;
}

interface BookingManagerProps {
  accessToken: string;
  language: 'he' | 'en' | 'ru';
}

const translations = {
  he: {
    title: 'ניהול תורים',
    loading: 'טוען...',
    noBookings: 'אין תורים להצגה',
    status: {
      pending: 'ממתין',
      confirmed: 'מאושר',
      cancelled: 'מבוטל',
      completed: 'הושלם',
    },
    actions: {
      confirm: 'אשר',
      cancel: 'בטל',
      complete: 'סיים',
      delete: 'מחק',
    },
    service: 'שירות',
    date: 'תאריך',
    time: 'שעה',
    customer: 'לקוח',
    phone: 'טלפון',
    email: 'אימייל',
    createdAt: 'נוצר ב',
    services: {
      basic: 'תספורת בסיסית',
      premium: 'תספורת פרימיום',
      spa: 'חבילת ספא',
      bath: 'רחצה בלבד',
      nails: 'חיתוך ציפורניים',
      teeth: 'ניקוי שיניים',
    }
  },
  en: {
    title: 'Bookings Management',
    loading: 'Loading...',
    noBookings: 'No bookings to display',
    status: {
      pending: 'Pending',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      completed: 'Completed',
    },
    actions: {
      confirm: 'Confirm',
      cancel: 'Cancel',
      complete: 'Complete',
      delete: 'Delete',
    },
    service: 'Service',
    date: 'Date',
    time: 'Time',
    customer: 'Customer',
    phone: 'Phone',
    email: 'Email',
    createdAt: 'Created',
    services: {
      basic: 'Basic Grooming',
      premium: 'Premium Grooming',
      spa: 'Spa Package',
      bath: 'Bath Only',
      nails: 'Nail Trimming',
      teeth: 'Teeth Cleaning',
    }
  },
  ru: {
    title: 'Управление записями',
    loading: 'Загрузка...',
    noBookings: 'Нет записей для отображения',
    status: {
      pending: 'В ожидании',
      confirmed: 'Подтверждено',
      cancelled: 'Отменено',
      completed: 'Завершено',
    },
    actions: {
      confirm: 'Подтвердить',
      cancel: 'Отменить',
      complete: 'Завершить',
      delete: 'Удалить',
    },
    service: 'Услуга',
    date: 'Дата',
    time: 'Время',
    customer: 'Клиент',
    phone: 'Телефон',
    email: 'Эл. почта',
    createdAt: 'Создано',
    services: {
      basic: 'Базовый груминг',
      premium: 'Премиум груминг',
      spa: 'Спа-пакет',
      bath: 'Только купание',
      nails: 'Стрижка когтей',
      teeth: 'Чистка зубов',
    }
  }
};

export function BookingManager({ accessToken, language }: BookingManagerProps) {
  const t = translations[language];
  const isRTL = language === 'he';
  
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const useLocal = isUsingFallback();

  const loadBookings = async () => {
    try {
      if (useLocal) {
        // Use IndexedDB (Dexie)
        const dbData = await dbBookings.getAll();
        // Map to component's expected format
        const mappedBookings: Booking[] = dbData.map((b: DbBooking) => ({
          id: b.id,
          name: b.name,
          phone: b.phone,
          email: b.email || '',
          service: b.service,
          date: b.date,
          time: b.time,
          status: b.status,
          createdAt: b.createdAt,
        }));
        setBookings(mappedBookings);
      } else {
        const response = await fetch(
          `${API_ENDPOINTS.bookings}`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to load bookings');
        }

        const data = await response.json();
        setBookings(data.bookings || []);
      }
    } catch (error) {
      // Failed to load bookings
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, [accessToken]);

  const updateStatus = async (bookingId: string, status: string) => {
    try {
      if (useLocal) {
        await dbBookings.update(bookingId, { status: status as any });
        await loadBookings();
      } else {
        const response = await fetch(
          `${API_ENDPOINTS.bookings}/${bookingId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ status }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to update booking');
        }

        await loadBookings();
      }
    } catch (error) {
      // Failed to update booking
    }
  };

  const deleteBooking = async (bookingId: string) => {
    if (!confirm('Are you sure you want to delete this booking?')) {
      return;
    }

    try {
      if (useLocal) {
        await dbBookings.delete(bookingId);
        await loadBookings();
      } else {
        const response = await fetch(
          `${API_ENDPOINTS.bookings}/${bookingId}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to delete booking');
        }

        await loadBookings();
      }
    } catch (error) {
      // Failed to delete booking
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#FFA500';
      case 'confirmed':
        return '#28A745';
      case 'cancelled':
        return '#DC3545';
      case 'completed':
        return '#6C757D';
      default:
        return '#444444';
    }
  };

  const getServiceName = (service: string) => {
    return t.services[service as keyof typeof t.services] || service;
  };

  if (loading) {
    return (
      <div className="text-center py-8" style={{ color: '#444444' }}>
        {t.loading}
      </div>
    );
  }

  return (
    <div className={isRTL ? 'text-right' : 'text-left'}>
      <h3 className="mb-6 flex items-center gap-2" style={{ color: '#444444' }}>
        <Calendar size={24} style={{ color: '#F29B70' }} />
        {t.title}
      </h3>

      {bookings.length === 0 ? (
        <div
          className="p-6 rounded-2xl text-center"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', color: '#444444' }}
        >
          {t.noBookings}
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <div
              key={booking.id}
              className="p-6 rounded-2xl"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}
            >
              {/* Header with Status */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <User size={20} style={{ color: '#F29B70' }} />
                  <span style={{ color: '#444444' }}>{booking.name}</span>
                </div>
                <div
                  className="px-3 py-1 rounded-full"
                  style={{ 
                    backgroundColor: getStatusColor(booking.status),
                    color: 'white',
                  }}
                >
                  {t.status[booking.status]}
                </div>
              </div>

              {/* Booking Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="flex items-center gap-2" style={{ color: '#444444' }}>
                  <Scissors size={16} style={{ color: '#F29B70' }} />
                  <span>{t.service}: {getServiceName(booking.service)}</span>
                </div>
                <div className="flex items-center gap-2" style={{ color: '#444444' }}>
                  <Calendar size={16} style={{ color: '#F29B70' }} />
                  <span>{t.date}: {booking.date}</span>
                </div>
                <div className="flex items-center gap-2" style={{ color: '#444444' }}>
                  <Clock size={16} style={{ color: '#F29B70' }} />
                  <span>{t.time}: {booking.time}</span>
                </div>
                <div className="flex items-center gap-2" style={{ color: '#444444' }}>
                  <Phone size={16} style={{ color: '#F29B70' }} />
                  <span>{booking.phone}</span>
                </div>
                {booking.email && (
                  <div className="flex items-center gap-2" style={{ color: '#444444' }}>
                    <Mail size={16} style={{ color: '#F29B70' }} />
                    <span>{booking.email}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {booking.status === 'pending' && (
                  <button
                    onClick={() => updateStatus(booking.id, 'confirmed')}
                    className="px-4 py-2 rounded-lg flex items-center gap-2 hover:shadow-md transition-all"
                    style={{ backgroundColor: '#28A745', color: 'white' }}
                  >
                    <CheckCircle size={16} />
                    {t.actions.confirm}
                  </button>
                )}
                {(booking.status === 'pending' || booking.status === 'confirmed') && (
                  <>
                    <button
                      onClick={() => updateStatus(booking.id, 'cancelled')}
                      className="px-4 py-2 rounded-lg flex items-center gap-2 hover:shadow-md transition-all"
                      style={{ backgroundColor: '#DC3545', color: 'white' }}
                    >
                      <XCircle size={16} />
                      {t.actions.cancel}
                    </button>
                    <button
                      onClick={() => updateStatus(booking.id, 'completed')}
                      className="px-4 py-2 rounded-lg flex items-center gap-2 hover:shadow-md transition-all"
                      style={{ backgroundColor: '#6C757D', color: 'white' }}
                    >
                      <CheckCircle size={16} />
                      {t.actions.complete}
                    </button>
                  </>
                )}
                <button
                  onClick={() => deleteBooking(booking.id)}
                  className="px-4 py-2 rounded-lg flex items-center gap-2 hover:shadow-md transition-all"
                  style={{ backgroundColor: '#F29B70', color: '#444444' }}
                >
                  <Trash2 size={16} />
                  {t.actions.delete}
                </button>
              </div>

              {/* Created timestamp */}
              <div className="mt-3 pt-3 border-t" style={{ borderColor: '#E0D5D1', color: '#999' }}>
                {t.createdAt}: {new Date(booking.createdAt).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}